import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { studentService, Student, PageResponse, StudentSearchRequest, StudentSearchFilters } from '../../services/studentService';
import { lessonService } from '../../services/lessonService';
import { profileService, Profile } from '../../services/profileService';
import { classeService, Classe } from '../../services/classeService';
import { useRights } from '../../hooks/useRights';
import { useHasAnyActionRight, studentActions } from '../../utils/actionRightsUtils';
import { ResourceType } from '../../services/billService';
import { useModal } from '../../context/ModalContext';
import { Lock, Receipt, Pencil, Trash2 } from 'lucide-react';
import '../../styles/index.css';
import './Students.css';
import TableActions from '../../components/TableActions';

const Students: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { schoolId, user, userType } = useAuth();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();
  const hasAnyActionRight = useHasAnyActionRight(studentActions());

  const [students, setStudents] = useState<Student[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [isDeepSearch, setIsDeepSearch] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [editForm, setEditForm] = useState({
    userName: '',
    profileId: '',
    classId: '',
    firstName: '',
    lastName: '',
    birthdate: '',
    enabled: true,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    userName: '',
    profileId: '',
    classId: '',
    firstName: '',
    lastName: '',
    birthdate: '',
    enabled: true,
  });
  const [resetPasswordStudent, setResetPasswordStudent] = useState<Student | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filters, setFilters] = useState({
    userName: '',
    firstName: '',
    lastName: '',
    classId: '',
    enabled: '',
  });

  useEffect(() => {
    loadClasses();
  }, []);

  // Effect to reload students when classId filter changes (especially for teachers)
  useEffect(() => {
    // Only auto-load if we have a classId set (for teachers) or if it's not a teacher
    if (userType !== 'TEACHER' || filters.classId) {
      loadStudents();
    }
  }, [filters.classId]);

  const loadProfiles = async () => {
    // Only load profiles if user has create or update permissions
    if (!hasRight('students:c') && !hasRight('students:u')) {
      return;
    }
    try {
      const data = await profileService.getAllProfiles();
      setProfiles(data);
    } catch (e: any) {
      console.error('Failed to load profiles:', e);
    }
  };

  const loadClasses = async () => {
    try {
      let data = await classeService.getAllClasses();

      // If teacher, filter classes to only those they teach
      if (userType === 'TEACHER' && user?.id) {
        try {
          const lessons = await lessonService.getLessonsByTeacher(user.id);
          const teacherClassIds = Array.from(new Set(lessons.map(l => l.classeId)));
          data = data.filter(c => c.id && teacherClassIds.includes(c.id));

          // Default to first class if available and no class selected
          if (data.length > 0 && !filters.classId) {
            const firstClassId = data[0].id;
            if (firstClassId) {
              setFilters(prev => ({ ...prev, classId: firstClassId }));
            }
          }
        } catch (err) {
          console.error('Failed to load teacher lessons', err);
        }
      }

      setClasses(data);
    } catch (e: any) {
      console.error('Failed to load classes:', e);
    }
  };

  const loadStudents = async (page: number = 0, reset: boolean = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(0);
      } else {
        setLoadingMore(true);
      }

      // For teachers, if no class selected, don't fetch (or fetch nothing)
      if (userType === 'TEACHER' && !filters.classId) {
        setStudents([]);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const searchRequest: StudentSearchRequest = {
        page,
        size: 100,
      };

      const response: PageResponse<Student> = await studentService.searchStudents(searchRequest);

      if (reset) {
        setStudents(response.content || []);
      } else {
        setStudents(prev => [...(prev || []), ...(response.content || [])]);
      }

      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load students');
      console.error('Load students error:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreStudents = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setLoadingMore(true);
      if (isDeepSearch) {
        // Load more with current filters
        const searchFilters: StudentSearchFilters = {
          userName: filters.userName || undefined,
          firstName: filters.firstName || undefined,
          lastName: filters.lastName || undefined,
          classId: filters.classId || undefined,
          enabled: filters.enabled !== '' ? filters.enabled === 'true' : undefined,
        };

        const searchRequest: StudentSearchRequest = {
          page: currentPage + 1,
          size: 100,
          filters: searchFilters,
        };

        studentService.searchStudents(searchRequest).then(response => {
          setStudents(prev => [...(prev || []), ...(response.content || [])]);
          setCurrentPage(response.page);
          setHasMore(response.hasNext);
          setTotalElements(response.totalElements);
          setLoadingMore(false);
        }).catch(e => {
          setError(e.response?.data?.message || 'Failed to load more students');
          setLoadingMore(false);
        });
      } else {
        loadStudents(currentPage + 1, false);
      }
    }
  }, [currentPage, hasMore, loadingMore, loading, isDeepSearch, filters]);

  const handleDeepSearch = async () => {
    try {
      setLoading(true);
      setIsDeepSearch(true);
      setCurrentPage(0);

      const searchFilters: StudentSearchFilters = {
        userName: filters.userName || undefined,
        firstName: filters.firstName || undefined,
        lastName: filters.lastName || undefined,
        classId: filters.classId || undefined,
        enabled: filters.enabled !== '' ? filters.enabled === 'true' : undefined,
      };

      const searchRequest: StudentSearchRequest = {
        page: 0,
        size: 100,
        filters: searchFilters,
      };

      const response: PageResponse<Student> = await studentService.searchStudents(searchRequest);
      setStudents(response.content || []);
      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to search students');
      console.error('Deep search error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents(0, true);
  }, []);

  const studentProfiles = useMemo(() => profiles.filter(p => p.type === 'STUDENT'), [profiles]);

  useEffect(() => {
    if (isCreating && studentProfiles.length > 0 && !createForm.profileId) {
      setCreateForm(prev => ({ ...prev, profileId: studentProfiles[0].id }));
    }
  }, [isCreating, studentProfiles, createForm.profileId]);

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const handleScroll = () => {
      const container = currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Trigger when scrolled to within 100px of bottom
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        loadMoreStudents();
      }
    };

    currentTarget.addEventListener('scroll', handleScroll);

    return () => {
      currentTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreStudents, hasMore, loadingMore, loading]);

  const formatBirthdateForInput = (birthdate: string | undefined): string => {
    if (!birthdate) return '';
    try {
      return birthdate.split('T')[0]; // Extract YYYY-MM-DD from ISO-8601
    } catch {
      return '';
    }
  };

  const formatBirthdateForDisplay = (birthdate: string | undefined): string => {
    if (!birthdate) return '-';
    try {
      return new Date(birthdate).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    const formattedBirthdate = formatBirthdateForInput(student.birthdate);
    setEditForm({
      userName: student.userName || '',
      profileId: student.profileId || '',
      classId: student.classId || '',
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      birthdate: formattedBirthdate,
      enabled: student.enabled ?? true,
    });
    // Load profiles only when editing (if not already loaded)
    if (profiles.length === 0) {
      loadProfiles();
    }
  };

  const handleCancelEdit = () => {
    setEditingStudent(null);
    setEditForm({
      userName: '',
      profileId: '',
      classId: '',
      firstName: '',
      lastName: '',
      birthdate: '',
      enabled: true,
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setCreateForm({
      userName: '',
      profileId: '',
      classId: '',
      firstName: '',
      lastName: '',
      birthdate: '',
      enabled: true,
    });
    // Load profiles only when creating (if not already loaded)
    if (profiles.length === 0) {
      loadProfiles();
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({
      userName: '',
      profileId: '',
      classId: '',
      firstName: '',
      lastName: '',
      birthdate: '',
      enabled: true,
    });
  };

  const handleSaveCreate = async () => {
    if (!createForm.userName.trim()) {
      setError(t('students.userNameRequired'));
      return;
    }
    if (!createForm.profileId) {
      setError(t('students.profileRequired'));
      return;
    }

    try {
      // Convert date string (YYYY-MM-DD) to ISO-8601 format for backend
      const birthdateISO = createForm.birthdate
        ? createForm.birthdate + 'T00:00:00.000Z'
        : undefined;

      await studentService.createStudent({
        userName: createForm.userName,
        profileId: createForm.profileId,
        classId: createForm.classId || undefined,
        firstName: createForm.firstName || undefined,
        lastName: createForm.lastName || undefined,
        birthdate: birthdateISO,
        enabled: createForm.enabled,
      });
      await loadStudents();
      handleCancelCreate();
      setError('');
    } catch (e: any) {
      setError(t('students.failedToCreate') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const handleSave = async () => {
    if (!editingStudent) return;
    if (!editForm.userName.trim()) {
      setError(t('students.userNameRequired'));
      return;
    }
    if (!editForm.profileId) {
      setError(t('students.profileRequired'));
      return;
    }

    try {
      // Convert date string (YYYY-MM-DD) to ISO-8601 format for backend
      const birthdateISO = editForm.birthdate
        ? editForm.birthdate + 'T00:00:00.000Z'
        : undefined;

      await studentService.updateStudent(editingStudent.id, {
        userName: editForm.userName,
        profileId: editForm.profileId,
        classId: editForm.classId || undefined,
        firstName: editForm.firstName || undefined,
        lastName: editForm.lastName || undefined,
        birthdate: birthdateISO,
        enabled: editForm.enabled,
      });
      await loadStudents();
      handleCancelEdit();
      setError('');
    } catch (e: any) {
      setError(t('students.failedToUpdate') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = (id: string) => {
    showConfirm(t('common.delete'), t('students.confirmDelete'), async () => {
      try {
        await studentService.deleteStudent(id);
        await loadStudents(0, true);
        setError('');
      } catch (e: any) {
        setError(t('students.failedToDelete') + ': ' + (e.response?.data?.message || e.message));
        showAlert(t('common.error'), t('students.failedToDelete'));
      }
    });
  };

  const getClassName = (classId?: string) => {
    if (!classId) return '-';
    const classe = classes.find(c => c.id === classId);
    return classe ? classe.name : classId;
  };


  // Client-side filtering only applies when not using deep search
  const filteredStudents = isDeepSearch ? (students || []) : (students || []).filter((student) => {
    if (filters.userName && !student.userName?.toLowerCase().includes(filters.userName.toLowerCase())) {
      return false;
    }
    if (filters.firstName && !student.firstName?.toLowerCase().includes(filters.firstName.toLowerCase())) {
      return false;
    }
    if (filters.lastName && !student.lastName?.toLowerCase().includes(filters.lastName.toLowerCase())) {
      return false;
    }
    if (filters.classId && student.classId !== filters.classId) {
      return false;
    }
    if (filters.enabled !== '' && student.enabled !== (filters.enabled === 'true')) {
      return false;
    }
    return true;
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setIsDeepSearch(false); // Reset deep search when filters change
  };

  const clearFilters = () => {
    setFilters({
      userName: '',
      firstName: '',
      lastName: '',
      classId: '',
      enabled: '',
    });
    setIsDeepSearch(false);
    loadStudents(0, true); // Reload all students
  };

  const handleResetPassword = (student: Student) => {
    setResetPasswordStudent(student);
    setResetPasswordValue('');
  };

  const handleCancelResetPassword = () => {
    setResetPasswordStudent(null);
    setResetPasswordValue('');
  };

  const handleSaveResetPassword = async () => {
    if (!resetPasswordStudent) return;
    if (!resetPasswordValue.trim()) {
      setError(t('students.passwordRequired'));
      return;
    }

    try {
      await studentService.resetPassword(resetPasswordStudent.id, resetPasswordValue);
      handleCancelResetPassword();
      setError('');
      showAlert(t('common.success'), t('students.passwordResetSuccess'));
    } catch (e: any) {
      setError(t('students.failedToResetPassword') + ': ' + (e.response?.data?.message || e.message));
      showAlert(t('common.error'), t('students.failedToResetPassword'));
    }
  };

  if (loading) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  const actions = (student: Student) => {
    const actions = [];

    if (hasRight('students:u')) {
      actions.push({
        label: t('common.edit'),
        onClick: () => handleEdit(student),
        className: 'btn-edit',
        icon: <Pencil size={16} />,
        hideLabelDesktop: true
      });
    }

    if (hasRight('admins:u')) {
      actions.push({
        label: t('students.resetPassword'),
        onClick: () => handleResetPassword(student),
        className: 'btn-reset-password',
        icon: <Lock size={16} />,
        hideLabelDesktop: true
      });
    }

    if (hasRight('bills:r')) {
      actions.push({
        label: t('students.viewBills'),
        onClick: () => navigate(`/bills?resourceType=STUDENT&resourceId=${student.id}`),
        className: 'btn-bills',
        icon: <Receipt size={16} />,
        hideLabelDesktop: true
      });
    }

    if (hasRight('students:d')) {
      actions.push({
        label: t('common.delete'),
        onClick: () => handleDelete(student.id),
        className: 'btn-delete',
        icon: <Trash2 size={16} />,
        hideLabelDesktop: true,
        variant: 'danger' as const
      });
    }
    return actions;
  };

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-toggle-sidebar"
            title={sidebarOpen ? t('common.hideFilters') : t('common.showFilters')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <h2>{t('students.title')}</h2>
        </div>
        {hasRight('students:c') && (
          <button onClick={handleCreate} className="btn-create">
            {t('students.createNew')}
          </button>
        )}
      </div>
      <div className="bo-content-wrapper">
        {sidebarOpen && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('students.filters')}</h3>
              <div className="sidebar-header-actions">
                <button onClick={handleDeepSearch} className="btn-deep-search">
                  {t('students.deepSearch')}
                </button>
                <button onClick={clearFilters} className="btn-clear-filters">
                  {t('common.clear')}
                </button>
              </div>
            </div>
            {isDeepSearch && (
              <div className="deep-search-indicator">
                <small>{t('students.deepSearchActive')} ({totalElements} {t('students.results')})</small>
              </div>
            )}
            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('students.userName')}</label>
                <input
                  type="text"
                  value={filters.userName}
                  onChange={(e) => handleFilterChange('userName', e.target.value)}
                  placeholder={t('students.filterByUserName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('students.firstName')}</label>
                <input
                  type="text"
                  value={filters.firstName}
                  onChange={(e) => handleFilterChange('firstName', e.target.value)}
                  placeholder={t('students.filterByFirstName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('students.lastName')}</label>
                <input
                  type="text"
                  value={filters.lastName}
                  onChange={(e) => handleFilterChange('lastName', e.target.value)}
                  placeholder={t('students.filterByLastName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('students.classId')}</label>
                <select
                  value={filters.classId}
                  onChange={(e) => handleFilterChange('classId', e.target.value)}
                >
                  <option value="">{t('students.allClasses')}</option>
                  {classes.map((classe) => (
                    <option key={classe.id} value={classe.id}>
                      {classe.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>{t('students.enabled')}</label>
                <select
                  value={filters.enabled}
                  onChange={(e) => handleFilterChange('enabled', e.target.value)}
                >
                  <option value="">{t('students.all')}</option>
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!sidebarOpen ? 'full-width' : ''}`}>
          {error && <div className="error-message">{error}</div>}

          {isCreating && (
            <div className="modal-overlay" onClick={handleCancelCreate}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{t('students.createStudent')}</h3>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>{t('students.userName')} *</label>
                    <input
                      type="text"
                      value={createForm.userName}
                      onChange={(e) => setCreateForm({ ...createForm, userName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('students.firstName')}</label>
                      <input
                        type="text"
                        value={createForm.firstName}
                        onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('students.lastName')}</label>
                      <input
                        type="text"
                        value={createForm.lastName}
                        onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('students.classId')}</label>
                      <select
                        value={createForm.classId}
                        onChange={(e) => setCreateForm({ ...createForm, classId: e.target.value })}
                      >
                        <option value="">Select class</option>
                        {classes.map((classe) => (
                          <option key={classe.id} value={classe.id}>
                            {classe.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('students.birthdate')}</label>
                      <input
                        type="date"
                        value={createForm.birthdate}
                        onChange={(e) => setCreateForm({ ...createForm, birthdate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('students.profile')} *</label>
                    <select
                      value={createForm.profileId}
                      onChange={(e) => setCreateForm({ ...createForm, profileId: e.target.value })}
                      required
                    >
                      <option value="">{t('students.selectProfile')}</option>
                      {studentProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                      <input
                        type="checkbox"
                        checked={createForm.enabled}
                        onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })}
                      />
                      {t('students.enabled')}
                    </label>
                  </div>
                  <div className="form-group password-hint">
                    <small className="hint-text">
                      {t('students.passwordHint')}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={handleSaveCreate} className="btn-primary">{t('common.create')}</button>
                  <button onClick={handleCancelCreate} className="btn-secondary">{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {editingStudent && (
            <div className="modal-overlay" onClick={handleCancelEdit}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{t('students.editStudent')}</h3>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>{t('students.userName')} *</label>
                    <input
                      type="text"
                      value={editForm.userName}
                      onChange={(e) => setEditForm({ ...editForm, userName: e.target.value })}
                      required
                      disabled
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('students.firstName')}</label>
                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('students.lastName')}</label>
                      <input
                        type="text"
                        value={editForm.lastName}
                        onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('students.classId')}</label>
                      <select
                        value={editForm.classId}
                        onChange={(e) => setEditForm({ ...editForm, classId: e.target.value })}
                      >
                        <option value="">Select class</option>
                        {classes.map((classe) => (
                          <option key={classe.id} value={classe.id}>
                            {classe.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('students.birthdate')}</label>
                      <input
                        type="date"
                        value={editForm.birthdate}
                        onChange={(e) => setEditForm({ ...editForm, birthdate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('students.profile')} *</label>
                    <select
                      value={editForm.profileId}
                      onChange={(e) => setEditForm({ ...editForm, profileId: e.target.value })}
                      required
                    >
                      <option value="">{t('students.selectProfile')}</option>
                      {studentProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                      <input
                        type="checkbox"
                        checked={editForm.enabled}
                        onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                      />
                      {t('students.enabled')}
                    </label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button onClick={handleSave} className="btn-primary">{t('common.save')}</button>
                  <button onClick={handleCancelEdit} className="btn-secondary">{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {resetPasswordStudent && (
            <div className="modal-overlay" onClick={handleCancelResetPassword}>
              <div className="form-card" onClick={(e) => e.stopPropagation()}>
                <div className="form-header">
                  <h3>{t('students.resetPassword')} - {resetPasswordStudent.userName}</h3>
                </div>
                <div className="form-body">
                  <div className="form-group">
                    <label>{t('students.newPassword')} *</label>
                    <input
                      type="password"
                      value={resetPasswordValue}
                      onChange={(e) => setResetPasswordValue(e.target.value)}
                      placeholder={t('students.newPasswordPlaceholder')}
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button onClick={handleSaveResetPassword} className="btn-primary">{t('common.save')}</button>
                    <button onClick={handleCancelResetPassword} className="btn-secondary">{t('common.cancel')}</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bo-table-container">
            {filteredStudents.length === 0 && !loading ? (
              <div className="no-data-message">
                <p>{t('students.noStudentsFound')}</p>
              </div>
            ) : (
              <>
                <div className="bo-table-wrapper" ref={observerTarget}>
                  <table className="bo-table">
                    <thead>
                      <tr>
                        <th>{t('students.userName')}</th>
                        <th>{t('students.firstName')}</th>
                        <th>{t('students.lastName')}</th>
                        <th>{t('students.classId')}</th>
                        <th>{t('students.birthdate')}</th>
                        <th>{t('students.enabled')}</th>
                        {hasAnyActionRight && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student) => (
                        <tr
                          key={student.id}
                          onClick={() => navigate(`/students/${student.id}`)}
                          className="student-row"
                        >
                          <td>{student.userName}</td>
                          <td>{student.firstName || '-'}</td>
                          <td>{student.lastName || '-'}</td>
                          <td>{getClassName(student.classId)}</td>
                          <td>{formatBirthdateForDisplay(student.birthdate)}</td>
                          <td>{student.enabled ? t('common.yes') : t('common.no')}</td>
                          {hasAnyActionRight && (
                            <td>
                              <TableActions actions={actions(student)} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="scroll-trigger">
                    {loadingMore && (
                      <div className="loading-more">
                        <small>{t('common.loading')}...</small>
                      </div>
                    )}
                    {!hasMore && students.length > 0 && (
                      <div className="no-more-data">
                        <small>{t('students.allLoaded')}</small>
                      </div>
                    )}
                  </div>
                </div>
                {!isDeepSearch && (
                  <div className="table-info">
                    <small>{t('students.showingLoaded')} ({students.length} {t('students.loaded')})</small>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default Students;
